// AGC: permissionHelper.ts
// AGC: Centralized permission management for Location, Camera, and Microphone.
// AGC: Single source of truth -- all components use this instead of implementing
// AGC: their own slightly different permission logic.
//
// AGC: Flow for each permission type:
//   1. checkPermissions()  -- read current OS state (fast, no dialog)
//   2. requestPermissions() -- show system dialog if needed
//   3. verify via checkPermissions() -- if requestPermissions threw, read real OS state
//   4. Return PermissionResult -- callers decide how to proceed
//   5. openAppSettings()  -- deep-link to app settings when permanently blocked
//
// AGC: Why no "optimistic true" on throws:
//   If requestPermissions() throws, we immediately re-call checkPermissions() to read
//   the real OS state. This avoids hiding genuine "don't ask again" denials while
//   still recovering from the known Capacitor plugin sync issue.
//
// AGC: Android 12+ approximate vs precise location:
//   checkPermissions() may return coarseLocation='granted' but location='denied'.
//   This means the user chose "Approximate" on Android 12+. We treat this as
//   granted=true, precise=false -- callers can use enableHighAccuracy=false.

import { Capacitor } from '@capacitor/core'
import { Geolocation } from '@capacitor/geolocation'
import { Camera } from '@capacitor/camera'
import { SpeechRecognition } from '@capacitor-community/speech-recognition'
import { NativeSettings, AndroidSettings } from 'capacitor-native-settings'

// AGC: Permission types supported by this helper
export type PermissionType = 'location' | 'camera' | 'microphone'

// AGC: Result returned by ensurePermission()
export interface PermissionResult {
  // AGC: True if permission is usable (even if only coarse for location)
  granted: boolean
  // AGC: Location-specific: true = ACCESS_FINE_LOCATION granted, false = coarse-only
  // AGC: Always true for camera/microphone
  precise: boolean
  // AGC: True if user permanently denied ("don't ask again") -- show settings deep-link
  blocked: boolean
  // AGC: Raw status string for logging/debugging
  status: string
}

// AGC: Custom error class thrown by nativeGeolocation.ts getCurrentPosition()
// AGC: when the permission is permanently blocked. Callers instanceof-check this
// AGC: to show PermissionBlockedModal instead of a generic error toast.
export class PermissionBlockedError extends Error {
  public readonly permissionType: PermissionType

  constructor(type: PermissionType) {
    const messages: Record<PermissionType, string> = {
      location:
        'Permissão de localização bloqueada. Abra as Configurações do app para habilitá-la.',
      camera:
        'Permissão de câmera bloqueada. Abra as Configurações do app para habilitá-la.',
      microphone:
        'Permissão de microfone bloqueada. Abra as Configurações do app para habilitá-la.',
    }
    super(messages[type])
    this.name = 'PermissionBlockedError'
    this.permissionType = type
  }
}

// AGC: Check if running on native Android/iOS
const isNative = (): boolean => Capacitor.isNativePlatform()

// =============================================================================
// AGC: Internal helpers
// =============================================================================

/**
 * AGC: Check and normalize the current location permission state.
 * AGC: Handles Android 12+ coarse vs fine distinction.
 */
const checkLocationPermission = async (): Promise<PermissionResult> => {
  try {
    const status = await Geolocation.checkPermissions()
    const fineGranted = status.location === 'granted'
    const coarseGranted = status.coarseLocation === 'granted'

    if (fineGranted) {
      return { granted: true, precise: true, blocked: false, status: 'fine-granted' }
    }
    if (coarseGranted) {
      // AGC: Android 12+ "Approximate" was chosen -- usable but imprecise
      console.log('AGC: PermissionHelper: coarse-only location (Android 12+ approximate)')
      return { granted: true, precise: false, blocked: false, status: 'coarse-only' }
    }

    const isDenied = status.location === 'denied'
    return {
      granted: false,
      precise: false,
      blocked: isDenied,
      status: status.location,
    }
  } catch (err) {
    console.error('AGC: PermissionHelper: checkLocationPermission error:', err)
    return { granted: false, precise: false, blocked: false, status: 'check-failed' }
  }
}

/**
 * AGC: Request location permission from the user.
 * AGC: Returns null if requestPermissions() throws (so caller can re-check).
 */
const requestLocationPermission = async (): Promise<PermissionResult | null> => {
  try {
    const status = await Geolocation.requestPermissions()
    const fineGranted = status.location === 'granted'
    const coarseGranted = status.coarseLocation === 'granted'

    if (fineGranted) {
      return { granted: true, precise: true, blocked: false, status: 'fine-granted' }
    }
    if (coarseGranted) {
      return { granted: true, precise: false, blocked: false, status: 'coarse-only' }
    }

    const isDenied = status.location === 'denied'
    return {
      granted: false,
      precise: false,
      blocked: isDenied,
      status: status.location,
    }
  } catch (err) {
    // AGC: Plugin can throw when permission state is inconsistent (known Capacitor issue).
    // AGC: Return null so caller falls back to checkPermissions() to read real OS state.
    console.warn('AGC: PermissionHelper: requestLocationPermission threw:', err)
    return null
  }
}

// =============================================================================
// AGC: Camera permission helpers (via @capacitor/camera plugin)
// =============================================================================

/**
 * AGC: Check current camera permission state via Capacitor Camera plugin.
 */
const checkCameraPermission = async (): Promise<PermissionResult> => {
  try {
    const status = await Camera.checkPermissions()
    if (status.camera === 'granted') {
      return { granted: true, precise: true, blocked: false, status: 'granted' }
    }
    const isDenied = status.camera === 'denied'
    return { granted: false, precise: false, blocked: isDenied, status: status.camera }
  } catch (err) {
    console.error('AGC: PermissionHelper: checkCameraPermission error:', err)
    return { granted: false, precise: false, blocked: false, status: 'check-failed' }
  }
}

/**
 * AGC: Request camera permission via Capacitor Camera plugin.
 * AGC: Returns null if requestPermissions() throws (so caller can re-check).
 */
const requestCameraPermission = async (): Promise<PermissionResult | null> => {
  try {
    const status = await Camera.requestPermissions({ permissions: ['camera'] })
    if (status.camera === 'granted') {
      return { granted: true, precise: true, blocked: false, status: 'granted' }
    }
    const isDenied = status.camera === 'denied'
    return { granted: false, precise: false, blocked: isDenied, status: status.camera }
  } catch (err) {
    console.warn('AGC: PermissionHelper: requestCameraPermission threw:', err)
    return null
  }
}

// =============================================================================
// AGC: Microphone permission helpers (via @capacitor-community/speech-recognition plugin)
// =============================================================================

/**
 * AGC: Check current microphone permission state via Speech Recognition plugin.
 * AGC: The plugin's speechRecognition permission maps to RECORD_AUDIO on Android.
 */
const checkMicrophonePermission = async (): Promise<PermissionResult> => {
  try {
    const status = await SpeechRecognition.checkPermissions()
    if (status.speechRecognition === 'granted') {
      return { granted: true, precise: true, blocked: false, status: 'granted' }
    }
    const isDenied = status.speechRecognition === 'denied'
    return { granted: false, precise: false, blocked: isDenied, status: status.speechRecognition }
  } catch (err) {
    console.error('AGC: PermissionHelper: checkMicrophonePermission error:', err)
    return { granted: false, precise: false, blocked: false, status: 'check-failed' }
  }
}

/**
 * AGC: Request microphone permission via Speech Recognition plugin.
 * AGC: Returns null if requestPermissions() throws (so caller can re-check).
 */
const requestMicrophonePermission = async (): Promise<PermissionResult | null> => {
  try {
    const status = await SpeechRecognition.requestPermissions()
    if (status.speechRecognition === 'granted') {
      return { granted: true, precise: true, blocked: false, status: 'granted' }
    }
    const isDenied = status.speechRecognition === 'denied'
    return { granted: false, precise: false, blocked: isDenied, status: status.speechRecognition }
  } catch (err) {
    console.warn('AGC: PermissionHelper: requestMicrophonePermission threw:', err)
    return null
  }
}

// =============================================================================
// AGC: Public API
// =============================================================================

/**
 * AGC: Check -> Request -> Verify flow for the given permission type.
 * AGC: Returns a PermissionResult describing the final state.
 * AGC: On web, always returns granted=true (browser handles its own permission dialog).
 */
export const ensurePermission = async (type: PermissionType): Promise<PermissionResult> => {
  // AGC: Web browser manages its own permissions via getUserMedia/geolocation API
  if (!isNative()) {
    return { granted: true, precise: true, blocked: false, status: 'web' }
  }

  if (type === 'location') {
    // AGC: Step 1 -- check current state
    const current = await checkLocationPermission()

    if (current.granted) {
      return current
    }

    // AGC: If already blocked (denied without rationale), skip the request dialog
    if (current.blocked) {
      console.warn('AGC: PermissionHelper: location is blocked (denied), skipping request')
      return current
    }

    // AGC: Step 2 -- request permission (shows system dialog if needed)
    const requested = await requestLocationPermission()

    if (requested !== null) {
      // AGC: requestPermissions() completed normally -- use its result
      if (!requested.granted) {
        console.warn('AGC: PermissionHelper: location request denied, status:', requested.status)
      }
      return requested
    }

    // AGC: Step 3 -- requestPermissions() threw; verify via checkPermissions() (read real OS state)
    console.log('AGC: PermissionHelper: requestPermissions threw, verifying via checkPermissions')
    const verified = await checkLocationPermission()
    return verified
  }

  // AGC: Camera permission via @capacitor/camera plugin
  if (type === 'camera') {
    const current = await checkCameraPermission()
    if (current.granted || current.blocked) return current

    const requested = await requestCameraPermission()
    if (requested !== null) {
      if (!requested.granted) {
        console.warn('AGC: PermissionHelper: camera request denied, status:', requested.status)
      }
      return requested
    }

    // AGC: requestPermissions() threw; verify via checkPermissions()
    console.log('AGC: PermissionHelper: camera requestPermissions threw, verifying via check')
    return await checkCameraPermission()
  }

  // AGC: Microphone permission via @capacitor-community/speech-recognition plugin
  if (type === 'microphone') {
    const current = await checkMicrophonePermission()
    if (current.granted || current.blocked) return current

    const requested = await requestMicrophonePermission()
    if (requested !== null) {
      if (!requested.granted) {
        console.warn('AGC: PermissionHelper: microphone request denied, status:', requested.status)
      }
      return requested
    }

    // AGC: requestPermissions() threw; verify via checkPermissions()
    console.log('AGC: PermissionHelper: microphone requestPermissions threw, verifying via check')
    return await checkMicrophonePermission()
  }

  return { granted: false, precise: false, blocked: false, status: 'unknown-type' }
}

/**
 * AGC: Open the app's settings page on Android/iOS so the user can manually
 * AGC: re-enable a blocked permission. Uses capacitor-native-settings plugin.
 */
export const openAppSettings = async (): Promise<void> => {
  try {
    if (isNative()) {
      await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails })
    } else {
      console.warn('AGC: PermissionHelper: openAppSettings() called on web -- no-op')
    }
  } catch (err) {
    console.error('AGC: PermissionHelper: openAppSettings failed:', err)
  }
}

/**
 * AGC: Determine if a getUserMedia error represents a real permission denial
 * AGC: (blocked/denied) vs some other error (device not found, hardware failure, etc.)
 */
export const isMediaPermissionError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false
  const name = (err as { name?: string }).name || ''
  const message = (err as { message?: string }).message || ''
  return (
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    message.toLowerCase().includes('permission') ||
    message.toLowerCase().includes('denied') ||
    message.toLowerCase().includes('not allowed')
  )
}
